/**
 * Flow-Builder logic (pure, no React)
 *
 * The conversation in step 3 is a fixed sequence of questions whose presence depends on what
 * the backend already knows (`GET /campaigns/:id/flow-builder`). Keeping the sequencing, the
 * answer → request mapping and the error decoding here means they can be unit-tested without
 * rendering anything.
 */

export type FlowAudience = 'new' | 'existing' | 'both';
export type LanguageChoice = 'keep' | 'primaryOnly' | 'auto';

export interface FlowBuilderAutoReplyCase {
  promptToIdentify: string;
  promptToReply: string;
}

export interface FlowBuilderContext {
  campaignId: string;
  campaignType: 'active_sourcing_co_pilot' | 'social_selling_co_pilot' | string;
  status: string;
  productType: 'normal' | 'sales_navigator' | 'recruiter' | null;
  inmailTouches: 0 | 1 | 2 | 3 | null;
  needsInMailCreditAnswer: boolean;
  recruitsFor: 'clients' | 'own' | null;
  needsRecruitsForAnswer: boolean;
  promptLanguage: 'de' | 'en';
  settings: { language: string; secondaryLanguage: string; formalSpeech: boolean };
  offer: Record<string, boolean>;
  missingBriefingFields: string[];
  customPlaceholders: Record<string, string>;
  autoReplyProposal: { isActive: boolean; goalofCommunication?: string; caseHandling: FlowBuilderAutoReplyCase[] };
  previous?: { answers: FlowBuilderAnswers; generatedAt: string; summary: FlowSummary } | null;
}

export interface FlowSummary {
  audience: FlowAudience;
  formalSpeech: boolean;
  promptLanguage: 'de' | 'en';
  inmailTouches: 0 | 1 | 2 | 3;
  recruitsFor: 'clients' | 'own';
  referralBonus?: string;
  stepCount: number;
  messageCount: number;
  inmailCount: number;
  hasWithdraw: boolean;
}

/** The request body of POST /campaigns/:id/flow-builder. Mirrors FlowBuilderAnswersSchema in the API. */
export interface FlowBuilderAnswers {
  audience: FlowAudience;
  formalSpeech: boolean;
  language?: 'de' | 'en' | 'auto';
  secondaryLanguage?: 'de' | 'en' | 'none';
  referral?: { enabled: boolean; bonus?: string };
  hasInMailCredit?: boolean;
  recruitsFor?: 'clients' | 'own';
  autoReply?: { isActive: boolean; goalofCommunication?: string; caseHandling?: FlowBuilderAutoReplyCase[] };
}

export type QuestionId = 'audience' | 'recruitsFor' | 'inmailCredit' | 'formality' | 'language' | 'referral' | 'autoReply';

/** What the user has answered so far in the panel (UI state, not the request body). */
export interface DraftAnswers {
  audience?: FlowAudience;
  recruitsFor?: 'clients' | 'own';
  hasInMailCredit?: boolean;
  formalSpeech?: boolean;
  language?: LanguageChoice;
  referralEnabled?: boolean;
  referralBonus?: string;
  autoReplyActive?: boolean;
  autoReplyGoal?: string;
  /** Indices into `context.autoReplyProposal.caseHandling` that stay selected. */
  autoReplyCases?: number[];
  /** UI only: the proposal is unfolded but not yet confirmed. */
  autoReplyDraftOpen?: boolean;
}

export function isRecruiting(context: Pick<FlowBuilderContext, 'campaignType'>): boolean {
  return context.campaignType === 'active_sourcing_co_pilot';
}

/**
 * The questions the panel has to ask for this campaign, in order. Questions the backend can
 * answer itself (licence, tenant profile) are skipped. The auto-reply question always comes
 * last because it is about what happens after a reply, and it is asked after the flow exists.
 */
export function questionSequence(context: FlowBuilderContext): QuestionId[] {
  const sequence: QuestionId[] = ['audience'];
  if (isRecruiting(context) && context.needsRecruitsForAnswer) sequence.push('recruitsFor');
  if (context.needsInMailCreditAnswer) sequence.push('inmailCredit');
  sequence.push('formality', 'language');
  if (isRecruiting(context)) sequence.push('referral');
  sequence.push('autoReply');
  return sequence;
}

/** Questions that must be answered before the flow can be generated (everything before auto-reply). */
export function questionsBeforeGeneration(context: FlowBuilderContext): QuestionId[] {
  return questionSequence(context).filter((q) => q !== 'autoReply');
}

export function isAnswered(question: QuestionId, draft: DraftAnswers): boolean {
  switch (question) {
    case 'audience':
      return draft.audience !== undefined;
    case 'recruitsFor':
      return draft.recruitsFor !== undefined;
    case 'inmailCredit':
      return draft.hasInMailCredit !== undefined;
    case 'formality':
      return draft.formalSpeech !== undefined;
    case 'language':
      return draft.language !== undefined;
    case 'referral':
      return draft.referralEnabled === false || (draft.referralEnabled === true && !!draft.referralBonus?.trim());
    case 'autoReply':
      return draft.autoReplyActive !== undefined;
    default:
      return false;
  }
}

/** First unanswered question of a sequence, or null when all are answered. */
export function nextQuestion(sequence: QuestionId[], draft: DraftAnswers): QuestionId | null {
  return sequence.find((q) => !isAnswered(q, draft)) ?? null;
}

/** Only ever call the API with what the panel actually asked; an unanswered field must not be guessed. */
export function toAnswers(context: FlowBuilderContext, draft: DraftAnswers, includeAutoReply: boolean): FlowBuilderAnswers {
  if (draft.audience === undefined || draft.formalSpeech === undefined) {
    throw new Error('Flow builder: audience and formality must be answered before generating');
  }
  const answers: FlowBuilderAnswers = { audience: draft.audience, formalSpeech: draft.formalSpeech };

  if (draft.language === 'primaryOnly') {
    const primary = context.settings.language === 'de' || context.settings.language === 'en' ? context.settings.language : context.promptLanguage;
    answers.language = primary;
    answers.secondaryLanguage = 'none';
  } else if (draft.language === 'auto') {
    answers.language = 'auto';
  }
  // 'keep' sends nothing: the campaign settings stay as they are.

  if (isRecruiting(context)) {
    if (draft.referralEnabled === true) {
      answers.referral = { enabled: true, bonus: draft.referralBonus?.trim() };
    } else if (draft.referralEnabled === false) {
      answers.referral = { enabled: false };
    }
    if (context.needsRecruitsForAnswer && draft.recruitsFor) answers.recruitsFor = draft.recruitsFor;
  }
  if (context.needsInMailCreditAnswer && draft.hasInMailCredit !== undefined) answers.hasInMailCredit = draft.hasInMailCredit;

  if (includeAutoReply && draft.autoReplyActive !== undefined) {
    if (draft.autoReplyActive) {
      const selected = (draft.autoReplyCases ?? []).map((i) => context.autoReplyProposal.caseHandling[i]).filter(Boolean);
      answers.autoReply = {
        isActive: true,
        goalofCommunication: (draft.autoReplyGoal ?? context.autoReplyProposal.goalofCommunication ?? '').trim(),
        caseHandling: selected,
      };
    } else {
      answers.autoReply = { isActive: false };
    }
  }
  return answers;
}

export const FLOW_BUILDER_ERROR_CODES = [
  'flow_builder_campaign_not_found',
  'flow_builder_campaign_not_draft',
  'flow_builder_account_missing',
  'flow_builder_briefing_incomplete',
  'flow_builder_inmail_credit_unknown',
  'flow_builder_recruits_for_unknown',
  'flow_builder_referral_bonus_missing',
  'flow_builder_auto_reply_incomplete',
  'flow_builder_generator_failed',
] as const;
export type FlowBuilderErrorCode = (typeof FLOW_BUILDER_ERROR_CODES)[number];

/** The API puts the code in square brackets at the end of the message; anything else is a generic error. */
export function decodeError(error: unknown): { code: FlowBuilderErrorCode | 'unknown'; message: string } {
  let message = '';
  if (error && typeof error === 'object') {
    const nested = (error as { error?: { message?: unknown } }).error;
    const nestedMessage = nested && typeof nested === 'object' ? nested.message : undefined;
    const ownMessage = (error as { message?: unknown }).message;
    if (typeof nestedMessage === 'string') message = nestedMessage;
    else if (typeof ownMessage === 'string') message = ownMessage;
  }
  const match = /\[(flow_builder_[a-z_]+)\]/.exec(message);
  const known = match !== null && (FLOW_BUILDER_ERROR_CODES as readonly string[]).includes(match[1]);
  const code: FlowBuilderErrorCode | 'unknown' = known ? (match![1] as FlowBuilderErrorCode) : 'unknown';
  return { code, message: message.replace(/\s*\[flow_builder_[a-z_]+\]\s*$/, '') };
}

/** Why the builder cannot run right now, or null when it can. Mirrors the backend's preconditions so the panel can say so before the first question. */
export function blockingReason(context: FlowBuilderContext): 'not_draft' | 'briefing_incomplete' | 'account_missing' | null {
  if (context.status !== 'draft') return 'not_draft';
  if (!context.productType) return 'account_missing';
  if (context.missingBriefingFields.length > 0) return 'briefing_incomplete';
  return null;
}

/** Chips for the summary card and for the greeting. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

export function summaryChips(context: FlowBuilderContext, summary: FlowSummary, t: Translate): string[] {
  const chips: string[] = [];
  chips.push(t(`flowBuilderAudience_${summary.audience}`));
  chips.push(summary.formalSpeech ? t('flowBuilderFormal') : t('flowBuilderCasual'));
  chips.push(summary.promptLanguage === 'de' ? t('flowBuilderLangDe') : t('flowBuilderLangEn'));
  if (summary.referralBonus) chips.push(t('flowBuilderChipReferral', { bonus: summary.referralBonus }));
  chips.push(t('flowBuilderChipInmails', { count: summary.inmailTouches }));
  if (isRecruiting(context)) chips.push(summary.recruitsFor === 'clients' ? t('flowBuilderChipClients') : t('flowBuilderChipOwn'));
  return chips;
}
